import { useState } from 'react';
import { Checkbox } from '@mui/material';
import { brand } from 'src/shared/branding/brand';
import { SmsConsentNote } from '../SmsConsentNote/SmsConsentNote';
import { SmsConsentBoxWrapper } from './SmsConsentBox.style';

const CHECKBOX_ID = 'smsConsentBox';

// The vendor's latest pick on the review step, kept for this browser tab until
// Submit saves it. Without it, a vendor who ticks the box, goes back a step
// and returns would see the box unchecked again.
const storageKey = (accountId: string | undefined) =>
  accountId ? `smsConsentChoice:${accountId}` : undefined;

const readPick = (key: string | undefined): boolean | undefined => {
  if (!key) return undefined;
  try {
    const value = sessionStorage.getItem(key);
    return value === 'true' ? true : value === 'false' ? false : undefined;
  } catch {
    return undefined;
  }
};

const writePick = (key: string | undefined, value: boolean) => {
  if (!key) return;
  try {
    sessionStorage.setItem(key, String(value));
  } catch {
    // Storage is blocked: the pick lasts while the step stays open.
  }
};

/**
 * The vendor's "Text message" choice on the sign-up review step.
 *
 * The box shows the vendor's pick on this step, else their pick earlier in this
 * browser tab, else unchecked. It never starts checked: carriers reject a text
 * opt-in that is ticked for the vendor. The account's saved value is not used,
 * because every new account is saved with texts on by default, so a saved "yes"
 * is not a choice the vendor made. `withChoice` adds the box to the account
 * save that Submit already sends. Under the PayUp brand it returns the save
 * unchanged.
 */
export const useSmsConsentChoice = (accountId: string | undefined) => {
  const [choice, setChoice] = useState<boolean | undefined>(undefined);
  const key = storageKey(accountId);
  const checked = choice ?? readPick(key) ?? false;
  const setChecked = (value: boolean) => {
    setChoice(value);
    writePick(key, value);
  };
  const withChoice = <T extends object>(payload: T): T =>
    brand.smsConsentRules ? { ...payload, is_sms_notification_active: checked } : payload;
  return { checked, setChecked, withChoice };
};

interface SmsConsentBoxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/**
 * The "Text message" checkbox with the consent line beside it. It shows only
 * on a brand with `smsConsentRules`; on PayUp it shows nothing. The review
 * steps place it at the end of the page content, so it stays in the page flow
 * above the terms sentence and never covers the review cards on a phone.
 */
export const SmsConsentBox = ({ checked, onChange }: SmsConsentBoxProps) => {
  if (!brand.smsConsentRules) return null;
  return (
    <SmsConsentBoxWrapper>
      <Checkbox
        id={CHECKBOX_ID}
        name="text"
        size="small"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <SmsConsentNote labelFor={CHECKBOX_ID} />
    </SmsConsentBoxWrapper>
  );
};
